import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { SITE, NAV } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { subscribeNewsletter } from "@/lib/api";
import Logo from "@/components/Logo";

const serviceLinks = NAV.find((n) => n.children)?.children || [];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're subscribed. Welcome to Beacon.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-navy text-white overflow-hidden" data-testid="footer">
      <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-teal/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 left-0 h-72 w-72 rounded-full bg-softblue-500/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        {/* Newsletter band */}
        <div className="grid gap-8 border-b border-white/10 py-14 lg:grid-cols-2 lg:items-center">
          <div>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight">Calm, practical insights — in your inbox.</h3>
            <p className="mt-3 text-white/60 max-w-md">Evidence-based tools for anxiety, mood, and everyday resilience. No spam, ever.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full max-w-md gap-3 lg:ml-auto" data-testid="newsletter-form">
            <Input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" data-testid="newsletter-input"
              className="h-13 py-6 rounded-full bg-white/10 border-white/15 text-white placeholder:text-white/40 focus-visible:ring-teal"
            />
            <Button type="submit" disabled={loading} data-testid="newsletter-submit"
              className="h-13 py-6 rounded-full bg-teal hover:bg-teal-dark px-6 shrink-0">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
            </Button>
          </form>
        </div>

        {/* Links */}
        <div className="grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to="/" className="flex items-center gap-3">
              <Logo className="h-11 w-11 shrink-0" tone="light" id="footer" />
              <span className="leading-none">
                <span className="block font-display text-xl tracking-tight text-white">Beacon</span>
                <span className="mt-0.5 block text-[0.6rem] font-semibold uppercase tracking-[0.24em] text-teal-light">Counseling · LLC</span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-white/55 leading-relaxed max-w-xs">
              {SITE.legal}. Connecticut's evidence-based Cognitive Behavioral Therapy practice.
            </p>
            <p className="mt-4 font-display text-lg italic text-white/80">"{SITE.tagline}"</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50">Services</h4>
            <ul className="mt-4 space-y-2.5">
              {serviceLinks.map((s) => (
                <li key={s.to}>
                  <Link to={s.to} className="text-sm text-white/70 hover:text-teal transition-colors">{s.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50">Explore</h4>
            <ul className="mt-4 space-y-2.5">
              {["/about", "/therapists", "/insurance-and-fees", "/resources", "/contact"].map((to) => {
                const label = NAV.find((n) => n.to === to)?.label || to;
                return <li key={to}><Link to={to} className="text-sm text-white/70 hover:text-teal transition-colors">{label}</Link></li>;
              })}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50">Get in touch</h4>
            <ul className="mt-4 space-y-3.5 text-sm">
              <li><a href={SITE.phoneHref} className="flex items-start gap-3 text-white/70 hover:text-teal transition-colors"><Phone className="h-4 w-4 mt-0.5 text-teal" />{SITE.phone}</a></li>
              <li><a href={SITE.emailHref} className="flex items-start gap-3 text-white/70 hover:text-teal transition-colors break-all"><Mail className="h-4 w-4 mt-0.5 text-teal" />{SITE.email}</a></li>
              <li className="flex items-start gap-3 text-white/70"><MapPin className="h-4 w-4 mt-0.5 text-teal" />{SITE.address}</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-white/10 py-8 text-xs text-white/45 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {SITE.legal}. All rights reserved.</p>
          <p className="max-w-xl">{SITE.crisis}</p>
        </div>
      </div>
    </footer>
  );
}
